import React, { useState } from "react";
import { useGetOrgIdentifier } from "../../generic/hooks/useGetOrgIdentifier";
import { useReadMemory } from "../../generic/hooks/useReadMemory";
import { useRecoilValue } from "recoil";
import MemoryType from "../../../service/memory/enum/MemoryType";
import { formSummaryDataAtom } from "../../../components/recoil/formSummaryRecoil";
import { currentConfiguration } from "../../../service/memory/recoil/configurationstore";
import { teamList } from "../../../components/recoil/teamsRecoil";
import FormService from "../../../components/generic/formbuilder/networkservice/FormService";
import ConfigurationConstants from "../../utils/configurationconstants";
import IntentType from "../../../intentexecutor/utils/IntentType";
import { performPostRequest } from "../../../service/network/NetworkService";
import { getApiUrl } from "../../../service/network/UrlMapper";
import {
  buildReportFileName,
  buildTaskReportRows,
  renderFormReportPdf,
} from "../../../components/generic/formbuilder/renderer/formReportPdf";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import { getFullName } from "../../careprovider/visits/utils";
import {
  dateAndTime,
  getDateString,
  getTimeSpentFromTimestamps,
  getTimeString,
} from "../../../utils/dateutils";
import SecondaryButton from "../../../components/generic/buttons/SecondaryButton";
import { v4 as uuidv4 } from "uuid";

const ZipExportButton = () => {
  const organizationIdentifier = useGetOrgIdentifier();
  const userDetails = useReadMemory(MemoryType.USER);
  const formSummaryData = useRecoilValue(formSummaryDataAtom);
  const configuration = useRecoilValue(currentConfiguration);
  const teams = useRecoilValue(teamList);
  const [loading, setLoading] = useState(false);
  const formService = new FormService();
  const secretKey =
    configuration[ConfigurationConstants.NIRAMAYA_ORGANIZATION_SECRETKEY];
  const formBucketName =
    configuration[ConfigurationConstants.NIRAMAYA_FORMS_BUCKETNAME];
  const formCache = new Map();

  const getFormJson = async (templateCode) => {
    if (formCache.has(templateCode)) {
      return formCache.get(templateCode);
    }

    const promise = (async () => {
      try {
        const { data } = await formService.getContentId(templateCode);

        if (data && formBucketName?.value) {
          const formJson = await formService.getJsonFromObjectStore(
            data.contentId,
            formBucketName.value
          );
          return {
            formJson,
            formName: data.title,
          };
        }
        return null;
      } catch (err) {
        console.error(err);
        return null;
      }
    })();

    formCache.set(templateCode, promise);

    return promise;
  };

  const getFormValues = async (formIdentifier, templateCode) => {
    const payload = {
      intent: IntentType.GET_FORMDATA,
      payload: {
        formIdentifier,
        templateCode,
        secretKey: secretKey?.key,
      },
      context: {
        teamIds: teams.map((team) => team?.id),
        organizationIdentifier,
        userIdentifier: userDetails?.userIdentifier,
        role: userDetails?.jobTitle,
      },
    };
    try {
      const result = await performPostRequest(
        getApiUrl()[IntentType.GET_FORMDATA],
        JSON.stringify(payload)
      );
      const resultJson = await result.json();

      if (resultJson[0]?.formData) {
        return JSON.parse(resultJson[0]?.formData);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const buildFormsAndExportPDF = async (data) => {
    if (!data?.length) return;

    const zip = new JSZip();

    for (const [index, task] of data.entries()) {
      try {
        const [formItem] = JSON.parse(task.formIdentifier);

        // Fetch form data
        const [formTemplateData, formValues] = await Promise.all([
          getFormJson(formItem.templateCode),
          getFormValues(formItem.formIdentifier, formItem.templateCode),
        ]);

        const formLabels = formTemplateData?.formJson?.components || [];

        const { reportInfoRows, patientVisitRows } = buildTaskReportRows(task, {
          getFullName,
          dateAndTime,
          getDateString,
          getTimeString,
          getTimeSpentFromTimestamps,
        });

        const doc = renderFormReportPdf({
          formName: formTemplateData?.formName,
          reportInfoRows,
          patientVisitRows,
          formLabels,
          formValues,
        });

        const pdfBytes = doc.output("arraybuffer");
        const baseName = buildReportFileName({
          medicalRecordNumber: task?.patient?.medicalRecordNumber,
          formName: formTemplateData?.formName,
          taskDate: task?.taskDate,
          getDateString,
        }).replace(/\.pdf$/, "");

        const fileName = `${baseName}_${uuidv4()}.pdf`;

        zip.file(fileName, pdfBytes);
      } catch (err) {
        console.error(`Failed to process task ${index + 1}`, err);
      }
    }

    // Generate ZIP
    const timestamp = new Date()
      .toISOString()
      .replace(/[:.]/g, "-")
      .replace("T", "_")
      .slice(0, 19);
    const content = await zip.generateAsync({ type: "blob" });
    saveAs(content, `Reports_${timestamp}.zip`);
  };

  const handleExportClick = async () => {
    setLoading(true);
    try {
      await buildFormsAndExportPDF(formSummaryData?.tasksSummary);
    } finally {
      setLoading(false);
    }
  };
  return (
    <SecondaryButton
      buttonText={"Export Zip"}
      loading={loading}
      handleOnClick={handleExportClick}
    />
  );
};

export default ZipExportButton;
